import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Grid,
  IconButton,
  TextField,
  Typography,
  debounce,
} from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import ButtonWithLoading from "components/ButtonWithLoading";
import { StyledPaper } from "components/Styled";
import { useSnackbar } from "notistack";
import { useCallback, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import apiRequest, { getErrorCode } from "utils/apiRequestWithErrorCode";
import isEmail from "utils/isEmail";
import AddIcon from "@material-ui/icons/Add";
import random from "utils/random";
import DeleteIcon from "@material-ui/icons/Delete";
import useSWR from "swr";
import { fetcher } from "utils/apiRequestWithErrorCode";
import { useTranslation } from "react-i18next";
import LinearLoader from "components/LinearLoader";
import {
  CreateEmailWithSmimeCertBody,
  EmailWithSmimeCerts,
  SmimeCert,
} from "@dashboard-v3/api";

type CertOption = {
  id: string;
  name: string;
};

type CertId = string;

type Selection = {
  key: string;
  isPrimary: boolean;
  certId: string;
  certName: string;
};

const EMPTY_VALUE = "EMPTY_VALUE";

export default function Loader({ action }: { action: "new" | "edit" }) {
  const { t } = useTranslation("smime");
  const params = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();

  const { data, isLoading } = useSWR<EmailWithSmimeCerts>(
    params.email ? `/smime/emails/${params.email}` : null,
    fetcher,
    {
      onError(err) {
        console.error(
          `An error occurred fetching GET /smime/emails/${params.email}`,
          err
        );
        enqueueSnackbar(t("error.fecthingAccounts"), {
          variant: "error",
        });
        history.push("/smime-certs?tab=accounts");
      },
    }
  );

  if (action === "new") return <Form />;
  if (action === "edit") {
    if (isLoading) return <LoadingForm />;
    return <Form data={data} />;
  }
}

function Form({ data }: { data?: EmailWithSmimeCerts }) {
  const { t } = useTranslation("smime");
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();
  const action = data ? "edit" : "new";

  const [email, setEmail] = useState(data?.email || "");
  const [emailError, setEmailError] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const [selections, setSelections] = useState<Selection[]>(() => {
    if (data && data.certificateHistory.length > 0) {
      return data.certificateHistory.map(cert => ({
        key: random(),
        certId: cert.certificateId,
        certName: cert.friendlyName,
        isPrimary: data.primaryCertificate?.certificateId === cert.certificateId,
      }));
    }
    return [];
  });

  const selectedCertIds = selections
    .filter(s => s.certId !== EMPTY_VALUE)
    .map(s => s.certId);

  const waitingSelection =
    selections.length && selections.at(-1).certId === EMPTY_VALUE;

  const canSave = email.trim() && !emailError && !isSaving && !waitingSelection;

  const cannotAddMore = Boolean(waitingSelection);

  const validateEmail = (value: string) => {
    if (!value.trim()) {
      return t("emailRequired");
    }
    if (!isEmail(value)) {
      return t("emailInvalid");
    }
    return "";
  };

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setEmail(value);
    setEmailError(validateEmail(value));
  };

  const handleSave = async () => {
    const emailValidation = validateEmail(email);
    if (emailValidation) {
      setEmailError(emailValidation);
      return;
    }

    const body: CreateEmailWithSmimeCertBody = { email: email.trim() };

    if (selections.length > 0) {
      const { certificateHistory, primary } = selections.reduce(
        (result, selection) => {
          if (selection.certId !== EMPTY_VALUE) {
            result.certificateHistory.push(selection.certId);
          }
          if (selection.isPrimary) {
            result.primary = selection.certId;
          }
          return result;
        },
        { certificateHistory: [] as CertId[], primary: null as CertId | null }
      );

      if (certificateHistory.length > 0) {
        body.certificateHistory = certificateHistory;
      }

      if (primary) {
        body.primaryCertificateId = primary;
      }
    }

    try {
      setIsSaving(true);

      if (action === "new") {
        await apiRequest("POST", "/smime/emails", body);
        enqueueSnackbar(t("accountCreated"), { variant: "success" });
      } else {
        await apiRequest("PUT", `/smime/emails/${data.email}`, body);
        enqueueSnackbar(t("accountUpdated"), { variant: "success" });
      }

      // Redirigir de vuelta a la lista de cuentas
      history.push("/smime-certs?tab=accounts");
    } catch (error) {
      const errorCode = getErrorCode(error);
      const message =
        errorCode === "ALREADY_EXISTS" ? t("emailExist") : t("errorGeneric");
      enqueueSnackbar(message, { variant: "error" });
    } finally {
      setIsSaving(false);
    }
  };

  function updateRow(key: string, certId: string, certName: string) {
    setSelections(selections => {
      const index = selections.findIndex(selection => selection.key === key);

      if (index !== -1) {
        const updated = [...selections];
        updated[index] = {
          ...updated[index],
          certId,
          certName,
        };
        return updated;
      }

      return selections;
    });
  }

  function updatePrimary(key: string, setAsPrimary: boolean) {
    setSelections(selections => {
      const updated = [...selections];

      // Remove previous primary
      if (setAsPrimary) {
        const prevPrimaryIndex = selections.findIndex(
          selection => selection.isPrimary
        );

        if (prevPrimaryIndex > -1) {
          updated[prevPrimaryIndex] = {
            ...updated[prevPrimaryIndex],
            isPrimary: false,
          };
        }
      }

      const index = selections.findIndex(selection => selection.key === key);
      if (index > -1) {
        updated[index] = { ...updated[index], isPrimary: setAsPrimary };
      }

      return updated;
    });
  }

  function addEmptyRow() {
    setSelections(selections => {
      const last = selections.at(-1);
      const newRow: Selection = {
        key: random(),
        certId: EMPTY_VALUE,
        certName: "",
        isPrimary: false,
      };

      if (last?.certId === EMPTY_VALUE) {
        return [...selections.slice(0, -1), newRow];
      }
      return [...selections, newRow];
    });
  }

  function removeRow(key: string) {
    setSelections(selections => {
      const index = selections.findIndex(selection => selection.key === key);
      if (index !== -1) {
        const updated = [...selections];
        updated.splice(index, 1);
        return updated;
      }
      return selections;
    });
  }

  return (
    <Grid container>
      <Grid item xs={8}>
        <StyledPaper>
          <form
            style={{ display: "flex", flexDirection: "column", gap: "15px" }}
            onSubmit={e => {
              e.preventDefault();
              handleSave();
            }}
          >
            <Typography variant="h6">{t("account")}</Typography>
            <FieldSet>
              {action === "new" && (
                <Typography variant="body1">{t("addEmailHint")}</Typography>
              )}

              <TextField
                label={t("form.email")}
                variant="outlined"
                fullWidth
                required
                value={email}
                onChange={handleEmailChange}
                error={!!emailError}
                helperText={emailError}
                disabled={Boolean(data) || isSaving}
              />
            </FieldSet>

            {selections.length === 0 && (
              <Button
                color="primary"
                startIcon={<AddIcon />}
                onClick={addEmptyRow}
                disabled={cannotAddMore}
                style={{ width: "max-content" }}
              >
                {t("attachCert")}
              </Button>
            )}

            {selections.length > 0 && (
              <FieldSet>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Typography style={{ fontWeight: 500 }}>
                    {t("linkedCerts")}
                  </Typography>

                  <Button
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={addEmptyRow}
                    disabled={cannotAddMore}
                    style={{ width: "max-content" }}
                  >
                    {t("addCert")}
                  </Button>
                </Box>

                <Box mt=".5rem">
                  <Box display="flex">
                    <Typography variant="body2" style={{ minWidth: "5rem" }}>
                      {t("principalCert")}
                    </Typography>
                    <Typography variant="body2">{t("cert")}</Typography>
                  </Box>

                  {selections.map(option => (
                    <CertRow
                      key={option.key}
                      selectedCert={
                        option.certId !== EMPTY_VALUE
                          ? { id: option.certId, name: option.certName }
                          : null
                      }
                      selectedCertIds={selectedCertIds}
                      isPrimary={option.isPrimary}
                      onSelect={(certId: string, certName: string) => {
                        updateRow(option.key, certId, certName);
                      }}
                      onPrimaryChange={checked => {
                        updatePrimary(option.key, checked);
                      }}
                      onRemove={() => removeRow(option.key)}
                    />
                  ))}
                </Box>
              </FieldSet>
            )}

            <Box display="flex" gridGap="1rem" alignItems="center" mt="1rem">
              <Box>
                <ButtonWithLoading
                  loading={isSaving}
                  variant="contained"
                  color="primary"
                  disableElevation
                  disabled={!canSave}
                  onClick={handleSave}
                  type="submit"
                >
                  {t("save")}
                </ButtonWithLoading>
              </Box>

              <Button onClick={() => history.goBack()}>{t("cancel")}</Button>
            </Box>
          </form>
        </StyledPaper>
      </Grid>
    </Grid>
  );
}

function CertRow({
  selectedCert,
  selectedCertIds,
  isPrimary,
  onSelect,
  onPrimaryChange,
  onRemove,
}: {
  selectedCert: CertOption | null;
  selectedCertIds: string[];
  isPrimary: boolean;
  onSelect: (certId: string, certName: string) => void;
  onPrimaryChange: (newValue: boolean) => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation("smime");
  const [options, setOptions] = useState<CertOption[]>(
    selectedCert ? [selectedCert] : []
  );
  const [loading, setLoading] = useState(false);
  const [initialLoaded, setInitialLoaded] = useState(false);

  async function fetchCerts(query?: string) {
    setLoading(true);
    try {
      let url = `/smime/certs?offset=0&limit=${query ? 20 : 10}`;
      if (query) url += `&query=${encodeURIComponent(query)}`;
      const results = await apiRequest<SmimeCert[]>("GET", url);
      setOptions(
        results.map(c => ({ id: c.certificateId, name: c.friendlyName }))
      );
    } catch {
      // Keep existing options on error
    } finally {
      setLoading(false);
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const searchCerts = useCallback(debounce(fetchCerts, 300), []);

  async function handleOpen() {
    if (!initialLoaded) {
      setInitialLoaded(true);
      await fetchCerts();
    }
  }

  return (
    <Box display="flex" alignItems="center" mt=".5rem">
      <Box style={{ minWidth: "5rem" }} alignItems="center">
        <Checkbox
          color="primary"
          name="primary"
          checked={isPrimary}
          onChange={(_, checked) => {
            onPrimaryChange(checked);
          }}
        />
      </Box>
      <Autocomplete<CertOption, false, false, false>
        fullWidth
        value={selectedCert}
        options={options}
        loading={loading}
        getOptionLabel={opt => opt?.name || ""}
        getOptionSelected={(opt, val) => opt.id === val.id}
        getOptionDisabled={opt =>
          selectedCertIds.includes(opt.id) && opt.id !== selectedCert?.id
        }
        filterOptions={x => x}
        noOptionsText={t("accounts.certSearch.noOptions")}
        onOpen={handleOpen}
        onInputChange={(_, value, reason) => {
          if (reason === "input") {
            if (value.length >= 2) {
              searchCerts(value);
            } else {
              setOptions(selectedCert ? [selectedCert] : []);
            }
          }
        }}
        onChange={(_, value) => {
          if (value) {
            onSelect(value.id, value.name);
          }
        }}
        renderInput={params => (
          <TextField
            {...params}
            variant="outlined"
            placeholder={t("accounts.certSearch.placeholder")}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading && (
                    <CircularProgress color="inherit" size={20} />
                  )}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
      <Box pl=".8rem">
        <IconButton size="small" aria-label="delete" onClick={onRemove}>
          <DeleteIcon />
        </IconButton>
      </Box>
    </Box>
  );
}

function FieldSet({ children }) {
  return (
    <Box mb=".5rem" display="flex" gridGap=".8rem" flexDirection="column">
      {children}
    </Box>
  );
}

function LoadingForm() {
  return (
    <Grid container>
      <Grid item xs={8}>
        <LinearLoader />
      </Grid>
    </Grid>
  );
}
